import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { KeyRound, LogIn } from "lucide-react";
import { FormProvider, useFormContext } from "react-hook-form";

import type { AuthSchemas } from "@/api/router/auth/schemas";
import { PulsoMark } from "@/components/layout/pulso-mark";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useChangePassword } from "@/hooks/use-change-password";
import { useLogin } from "@/hooks/use-login";
import { AuthLabels } from "@/lib/labels";

type LoginInput = typeof AuthSchemas.login.infer;
type ChangeInput = { new_password: string; confirm: string };

const searchSchema = type({
	"change?": "boolean",
});

export const Route = createFileRoute("/login")({
	validateSearch: searchSchema,
	component: LoginPage,
});

function LoginPage() {
	const { change } = Route.useSearch();

	return (
		<div
			data-component="login-page"
			className="flex min-h-dvh items-center justify-center bg-background p-4"
		>
			<Card className="w-full max-w-md border-t-4 border-t-primary p-2 sm:p-4">
				<div className="px-4 pt-4 sm:px-5">
					<span className="grid size-12 place-items-center rounded-lg bg-primary-soft text-primary">
						<PulsoMark className="size-7" />
					</span>
				</div>
				{!change && <LoginForm />}
				{!!change && <ChangePasswordForm />}
			</Card>
		</div>
	);
}

function LoginForm() {
	const { methods, handleSubmit } = useLogin();

	return (
		<>
			<CardHeader>
				<h1 className="font-display text-3xl font-medium">{AuthLabels.loginTitle}</h1>
				<CardDescription>{AuthLabels.loginSubtitle}</CardDescription>
			</CardHeader>

			<CardContent>
				<FormProvider {...methods}>
					<form onSubmit={handleSubmit} className="space-y-4">
						<LoginName />
						<LoginPassword />
						<LoginSubmit />
					</form>
				</FormProvider>
			</CardContent>
		</>
	);
}

function LoginName() {
	const {
		register,
		formState: { errors },
	} = useFormContext<LoginInput>();

	const nameError = errors.name?.message;

	return (
		<div className="space-y-2">
			<Label htmlFor="name">Nome</Label>

			<Input
				id="name"
				type="text"
				data-slot="name-input"
				aria-describedby={nameError ? "login-name-error" : undefined}
				aria-invalid={!!nameError}
				{...register("name")}
				required
			/>

			{!!nameError && (
				<p
					id="login-name-error"
					role="alert"
					data-component="login-name-error"
					className="text-sm text-destructive"
				>
					{nameError}
				</p>
			)}
		</div>
	);
}

function LoginPassword() {
	const {
		register,
		formState: { errors },
	} = useFormContext<LoginInput>();
	const passwordError = errors.password?.message;

	return (
		<div className="space-y-2">
			<Label htmlFor="password">Senha</Label>

			<Input
				id="password"
				type="password"
				data-slot="password-input"
				aria-describedby={passwordError ? "login-password-error" : undefined}
				aria-invalid={!!passwordError}
				{...register("password")}
				required
			/>

			{!!passwordError && (
				<p
					id="login-password-error"
					role="alert"
					data-component="login-password-error"
					className="text-sm text-destructive"
				>
					{passwordError}
				</p>
			)}
		</div>
	);
}

function LoginSubmit() {
	const {
		formState: { isSubmitting },
	} = useFormContext<LoginInput>();

	return (
		<Button
			type="submit"
			data-slot="submit"
			data-submitting={isSubmitting}
			aria-busy={isSubmitting}
			className="w-full"
			disabled={isSubmitting}
		>
			<LogIn className="size-4" />
			{isSubmitting && AuthLabels.signingIn}
			{!isSubmitting && AuthLabels.signIn}
		</Button>
	);
}

function ChangePasswordForm() {
	const { methods, handleSubmit } = useChangePassword();

	return (
		<>
			<CardHeader>
				<h1 className="font-display text-3xl font-medium">{AuthLabels.changeTitle}</h1>
				<CardDescription>{AuthLabels.changeSubtitle}</CardDescription>
			</CardHeader>

			<CardContent>
				<FormProvider {...methods}>
					<form onSubmit={handleSubmit} className="space-y-4">
						<ChangeNew />
						<ChangeConfirm />
						<ChangeSubmit />
					</form>
				</FormProvider>
			</CardContent>
		</>
	);
}

function ChangeNew() {
	const {
		register,
		formState: { errors },
	} = useFormContext<ChangeInput>();
	const error = errors.new_password?.message;

	return (
		<div className="space-y-2">
			<Label htmlFor="new_password">{AuthLabels.newPassword}</Label>

			<Input
				id="new_password"
				type="password"
				data-slot="new-password-input"
				aria-describedby={error ? "change-new-error" : undefined}
				aria-invalid={!!error}
				{...register("new_password")}
				required
				minLength={8}
			/>

			{!!error && (
				<p
					id="change-new-error"
					role="alert"
					data-component="change-new-error"
					className="text-sm text-destructive"
				>
					{error}
				</p>
			)}
		</div>
	);
}

function ChangeConfirm() {
	const {
		register,
		formState: { errors },
	} = useFormContext<ChangeInput>();
	const error = errors.confirm?.message;

	return (
		<div className="space-y-2">
			<Label htmlFor="confirm">{AuthLabels.confirmPassword}</Label>

			<Input
				id="confirm"
				type="password"
				data-slot="confirm-input"
				aria-describedby={error ? "change-confirm-error" : undefined}
				aria-invalid={!!error}
				{...register("confirm")}
				required
				minLength={8}
			/>

			{!!error && (
				<p
					id="change-confirm-error"
					role="alert"
					data-component="change-confirm-error"
					className="text-sm text-destructive"
				>
					{error}
				</p>
			)}
		</div>
	);
}

function ChangeSubmit() {
	const {
		formState: { isSubmitting },
	} = useFormContext<ChangeInput>();

	return (
		<Button
			type="submit"
			data-slot="change-submit"
			data-submitting={isSubmitting}
			aria-busy={isSubmitting}
			className="w-full"
			disabled={isSubmitting}
		>
			<KeyRound className="size-4" />
			{isSubmitting && AuthLabels.savingPassword}
			{!isSubmitting && AuthLabels.saveAndEnter}
		</Button>
	);
}
